// File: src/components/pages/InstructorConfiguration.jsx
// Instructor-specific configuration settings
// UPDATED: Split advance bookings into instructor and student fields

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Settings, Save, AlertCircle, CheckCircle, Eye } from 'lucide-react';
import { instructorConfigAPI } from '../../services/api';
import { AUTH0_NAMESPACE } from '../../utils/constants';
import '../../styles/unified.css';

export default function InstructorConfiguration() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');
  const instructorId = user?.sub;

  // State for configuration - UPDATED with split advance bookings
  const [config, setConfig] = useState({
    allowedAdvanceBookingsInstructor: 1,
    allowedAdvanceBookingsStudent: 1,
    lessonDuration: 2,
    maxTrackerLevels: 5,
    hourlyRateAutomatic: 45,
    hourlyRateManual: 40,
    discountIntroductory: 10,
    discount6HoursPlus: 2,
    discountFixedPercentage: null,
    discountFixedAmount: null
  });

  const [originalConfig, setOriginalConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isServiceProvider && instructorId) {
      loadConfiguration();
    }
  }, [isServiceProvider, instructorId]);

  // Check for changes
  useEffect(() => {
    if (originalConfig) {
      const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
      setHasChanges(changed);
    }
  }, [config, originalConfig]);

  /**
   * Load instructor configuration
   */
  const loadConfiguration = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await instructorConfigAPI.getConfiguration(getAccessTokenSilently);

      if (response.success && response.data) {
        // Handle backward compatibility - if old field exists, split it
        let configData = { ...response.data };
        
        // If old field exists but new fields don't, migrate the value
        if (configData.allowedAdvanceBookings !== undefined && 
            configData.allowedAdvanceBookingsInstructor === undefined) {
          configData.allowedAdvanceBookingsInstructor = configData.allowedAdvanceBookings;
          configData.allowedAdvanceBookingsStudent = configData.allowedAdvanceBookings;
          delete configData.allowedAdvanceBookings; // Remove old field
        }
        
        setConfig(configData);
        setOriginalConfig(configData);
      } else {
        // Configuration doesn't exist yet - use defaults
        setOriginalConfig(config);
      }
    } catch (err) {
      console.error('Failed to load configuration:', err);
      setError('Failed to load configuration. Using default values.');
      setOriginalConfig(config);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle configuration field change
   */
  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Save configuration with confirmation
   */
  const handleSave = async () => {
    // Show confirmation dialog
    const confirmMessage = 
      'Are you sure you want to update your configuration settings? ' +
      'These changes will affect how bookings and pricing are calculated.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Remove old field if it exists (for backward compatibility)
      const configToSave = { ...config };
      delete configToSave.allowedAdvanceBookings;
      
      const response = await instructorConfigAPI.updateConfiguration(
        configToSave,
        getAccessTokenSilently
      );

      if (response.success) {
        setSuccess('Configuration saved successfully!');
        setOriginalConfig(config);
        setHasChanges(false);

        // Auto-hide success message
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setError(`Failed to save configuration: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Reset to original values
   */
  const handleReset = () => {
    if (window.confirm('Are you sure you want to discard your changes?')) {
      setConfig(originalConfig);
      setHasChanges(false);
    }
  };

  // Generate number array for dropdowns
  const generateNumberArray = (start, end, interval = 1) => {
    const arr = [];
    for (let i = start; i <= end; i += interval) {
      arr.push(i);
    }
    return arr;
  };

  // Access control
  if (!isServiceProvider) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to instructors</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bookings-container">
        <div className="loading-state">
          <Settings size={48} className="loading-icon" />
          <h2>Loading configuration...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration-container-final">
      {/* Success/Error Messages */}
      {success && (
        <div className="booking-success-banner">
          <CheckCircle size={24} />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="configuration-header-final">
        <div className="header-title-final">
          <Settings size={28} />
          <div>
            <h2>Instructor Configuration</h2>
            <p className="header-subtitle-final">
              Configure booking limits, hourly rates, and discount settings
            </p>
          </div>
        </div>
      </div>

      {/* Two Panel Layout */}
      <div className="configuration-panels-final">
        {/* LEFT PANEL - Editable Form with Grid Layout */}
        <div className="configuration-panel-left-final">
          <div className="panel-header-final">
            <Settings size={20} />
            <h3>Edit Configuration</h3>
          </div>

          <div className="configuration-form-final">
            {/* Allowed Advance Bookings Section - Horizontal Line */}
            <div className="config-section-divider-final">
              <span className="section-title-final">Allowed Number of Advance Bookings</span>
            </div>

            {/* Allowed Advance Bookings - Two Columns */}
            <div className="config-grid-2col-final">
              <div className="config-field-final">
                <label htmlFor="allowedAdvanceBookingsInstructor" className="config-label-final">
                  For Instructor
                </label>
                <select
                  id="allowedAdvanceBookingsInstructor"
                  className="config-select-final"
                  value={config.allowedAdvanceBookingsInstructor}
                  onChange={(e) => handleChange('allowedAdvanceBookingsInstructor', parseInt(e.target.value))}
                >
                  {generateNumberArray(0, 10).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              <div className="config-field-final">
                <label htmlFor="allowedAdvanceBookingsStudent" className="config-label-final">
                  For Student
                </label>
                <select
                  id="allowedAdvanceBookingsStudent"
                  className="config-select-final"
                  value={config.allowedAdvanceBookingsStudent}
                  onChange={(e) => handleChange('allowedAdvanceBookingsStudent', parseInt(e.target.value))}
                >
                  {generateNumberArray(0, 10).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>

            <br></br>

            {/* Hourly Rates Section - Horizontal Line */}
            <div className="config-section-divider-final">
              <span className="section-title-final">Hourly Rates (£)</span>
            </div>

            {/* Hourly Rates - Two Columns */}
            <div className="config-grid-2col-final">
              <div className="config-field-final">
                <label htmlFor="hourlyRateAutomatic" className="config-label-final">
                  Hourly Rate – Automatic
                </label>
                <select
                  id="hourlyRateAutomatic"
                  className="config-select-final"
                  value={config.hourlyRateAutomatic}
                  onChange={(e) => handleChange('hourlyRateAutomatic', parseInt(e.target.value))}
                >
                  {generateNumberArray(0, 100, 5).map(num => (
                    <option key={num} value={num}>£{num}</option>
                  ))}
                </select>
              </div>

              <div className="config-field-final">
                <label htmlFor="hourlyRateManual" className="config-label-final">
                  Hourly Rate – Manual
                </label>
                <select
                  id="hourlyRateManual"
                  className="config-select-final"
                  value={config.hourlyRateManual}
                  onChange={(e) => handleChange('hourlyRateManual', parseInt(e.target.value))}
                >
                  {generateNumberArray(0, 100, 5).map(num => (
                    <option key={num} value={num}>£{num}</option>
                  ))}
                </select>
              </div>
            </div>
            <br></br>

            {/* Lesson Duration Section - Horizontal Line */}
            <div className="config-section-divider-final">
              <span className="section-title-final">Lesson Duration</span>
            </div>

            {/* Lesson Duration - Single Column */}
            <div className="config-field-final">
              <label htmlFor="lessonDuration" className="config-label-final">
                Default Lesson Duration
              </label>
              <select
                id="lessonDuration"
                className="config-select-final"
                value={config.lessonDuration}
                onChange={(e) => handleChange('lessonDuration', parseFloat(e.target.value))}
              >
                <option value={1}>1 hour</option>
                <option value={1.5}>1.5 hours</option>
                <option value={2} selected>2 hours</option>
              </select>
            </div>
            <br></br>

            {/* Tracker Levels Section */}
            <div className="config-section-divider-final">
              <span className="section-title-final">Tracker Levels</span>
            </div>

            <div className="config-field-final">
              <label htmlFor="maxTrackerLevels" className="config-label-final">
                Max Tracker Levels
              </label>
              <select
                id="maxTrackerLevels"
                className="config-select-final"
                value={config.maxTrackerLevels || 5}
                onChange={(e) => handleChange('maxTrackerLevels', parseInt(e.target.value))}
              >
                {generateNumberArray(1, 10).map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            <br></br>

            {/* Discounts Section - Horizontal Line */}
            <div className="config-section-divider-final">
              <span className="section-title-final">Discounts</span>
            </div>

            {/* Discounts - Two Columns (2x2 Grid) */}
            <div className="config-grid-2col-final">
              <div className="config-field-final">
                <label htmlFor="discountIntroductory" className="config-label-final">
                  Discount – Introductory
                </label>
                <select
                  id="discountIntroductory"
                  className="config-select-final"
                  value={config.discountIntroductory}
                  onChange={(e) => handleChange('discountIntroductory', parseInt(e.target.value))}
                >
                  {generateNumberArray(1, 15).map(num => (
                    <option key={num} value={num}>{num}%</option>
                  ))}
                </select>
              </div>

              <div className="config-field-final">
                <label htmlFor="discount6HoursPlus" className="config-label-final">
                  Discount – 6 Hours Plus (£ per hour)
                </label>
                <select
                  id="discount6HoursPlus"
                  className="config-select-final"
                  value={config.discount6HoursPlus}
                  onChange={(e) => handleChange('discount6HoursPlus', parseInt(e.target.value))}
                >
                  {generateNumberArray(0, 10).map(num => (
                    <option key={num} value={num}>£{num}</option>
                  ))}
                </select>
              </div>

              <div className="config-field-final">
                <label htmlFor="discountFixedPercentage" className="config-label-final">
                  Discount – Fixed Percentage
                </label>
                <select
                  id="discountFixedPercentage"
                  className="config-select-final"
                  value={config.discountFixedPercentage || ''}
                  onChange={(e) => handleChange('discountFixedPercentage', 
                    e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">None</option>
                  {generateNumberArray(1, 10).map(num => (
                    <option key={num} value={num}>{num}%</option>
                  ))}
                </select>
              </div>

              <div className="config-field-final">
                <label htmlFor="discountFixedAmount" className="config-label-final">
                  Discount – Fixed Amount (£)
                </label>
                <select
                  id="discountFixedAmount"
                  className="config-select-final"
                  value={config.discountFixedAmount || ''}
                  onChange={(e) => handleChange('discountFixedAmount', 
                    e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">None</option>
                  {generateNumberArray(1, 20).map(num => (
                    <option key={num} value={num}>£{num}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {hasChanges && (
            <div className="configuration-actions-final">
              <button
                className="btn-secondary-final"
                onClick={handleReset}
                disabled={isSaving}
              >
                Reset
              </button>
              <button
                className="btn-primary-final"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Settings className="btn-icon spinning" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="btn-icon" size={18} />
                    Save
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* RIGHT PANEL - Read-Only Summary */}
        <div className="configuration-panel-right-final">
          <div className="panel-header-final">
            <Eye size={20} />
            <h3>Current Configuration</h3>
          </div>

          <div className="config-summary-card-final">
            {/* Booking Settings */}
            <div className="summary-section-final">
              <h4 className="summary-section-title-final">Booking Settings</h4>
              <div className="summary-item-final">
                <span className="summary-label-final">Advance Bookings (Instructor)</span>
                <span className="summary-value-final">
                  {originalConfig?.allowedAdvanceBookingsInstructor ?? 1}
                </span>
              </div>
              <div className="summary-item-final">
                <span className="summary-label-final">Advance Bookings (Student)</span>
                <span className="summary-value-final">
                  {originalConfig?.allowedAdvanceBookingsStudent ?? 1}
                </span>
              </div>
            </div>

            {/* Hourly Rates */}
            <div className="summary-section-final">
              <h4 className="summary-section-title-final">Hourly Rates</h4>
              <div className="summary-item-final">
                <span className="summary-label-final">Automatic</span>
                <span className="summary-value-final">£{originalConfig?.hourlyRateAutomatic ?? 45}/hr</span>
              </div>
              <div className="summary-item-final">
                <span className="summary-label-final">Manual</span>
                <span className="summary-value-final">£{originalConfig?.hourlyRateManual ?? 40}/hr</span>
              </div>
            </div>

            {/* Lesson Duration */}
            <div className="summary-section-final">
              <h4 className="summary-section-title-final">Lesson Duration</h4>
              <div className="summary-item-final">
                <span className="summary-label-final">Default Duration</span>
                <span className="summary-value-final">
                  {originalConfig?.lessonDuration === 1.5 
                    ? '1.5 hours' 
                    : `${originalConfig?.lessonDuration ?? 1} hour${originalConfig?.lessonDuration === 2 ? 's' : ''}`
                  }
                </span>
              </div>
            </div>

            {/* Discounts */}
            <div className="summary-section-final">
              <h4 className="summary-section-title-final">Discounts</h4>
              <div className="summary-item-final">
                <span className="summary-label-final">Introductory</span>
                <span className="summary-value-final">{originalConfig?.discountIntroductory ?? 10}%</span>
              </div>
              <div className="summary-item-final">
                <span className="summary-label-final">6+ Hours</span>
                <span className="summary-value-final">£{originalConfig?.discount6HoursPlus ?? 2}/hr</span>
              </div>
              {originalConfig?.discountFixedPercentage && (
                <div className="summary-item-final">
                  <span className="summary-label-final">Fixed %</span>
                  <span className="summary-value-final">{originalConfig.discountFixedPercentage}%</span>
                </div>
              )}
              {originalConfig?.discountFixedAmount && (
                <div className="summary-item-final">
                  <span className="summary-label-final">Fixed Amount</span>
                  <span className="summary-value-final">£{originalConfig.discountFixedAmount}</span>
                </div>
              )}
            </div>

            {/* Change Indicator */}
            {hasChanges && (
              <div className="summary-changes-notice-final">
                <AlertCircle size={16} />
                <span>You have unsaved changes</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}